import React, { useEffect, useState } from "react";
import { Flex, ProgressCircle, Text, View } from "@adobe/react-spectrum";
import "./style.css";
import {
  callMonitorRequest,
  supervisorCallDetails,
} from "../../api/session-api";
import CallMonitor from "./CallMonitor";
import storeRTK from "../../store/storeRTK";
import { setSelectedTab } from "../../actions/common";
import { AgentStatusDetail, BusinessAttributes } from "../../types/agent.type";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import ActiveCallContainer from "../CallTab/CallDialer/ActiveCallContainer";
import { formatLabel, showErrorToast } from "../../utils/utils";
import { fixJson, formatTimestamp } from "../../utils";
import Emitter from "../../emitter";
import { TELEMETRY_ERROR, MONITORING_ERROR } from "../../constants/events";
import { API_CALL_BARGE } from "../../amazon-connect/constants";

interface SupervisorCallDetailsProps {
  agentList: any;
  selectedAgent: string;
}

const SupervisorCallDetails: React.FC<SupervisorCallDetailsProps> = ({
  agentList,
  selectedAgent,
}) => {
  const [agentDetails, setAgentDetails] = useState<AgentStatusDetail | null>(
    null
  );
  const [monitorType, setMonitorType] = useState("silent");
  const [isDisabled, setIsDisabled] = useState(true);
  const [callDetails, setCallDetails] = useState<{
    attributes: Record<string, any>;
  } | null>(null);
  const [muteStatus, setMuteStatus] = useState(true)
  const [activeContactId, setActiveContactId] = useState("");

  const agent = useSelector((state: RootState) => state.agent);
  let agentName = agent.id.split("@")[0];
  const call = useSelector((state: RootState) => state.call);
  let agentBusinessAttributes = agent?.businessAttributes || [];
  let callCustomerAttributes = callDetails ? callDetails.attributes : {};
  let notesPresent = false;
  let notesPropertySpelling = "";
  let vpnStatus = useSelector(
    (state: RootState) => state.webrtc?.isVoiceNetworkAvailable
  );

  // 1. use filter to compare agent attributes with call attributes. Get matching attributes as output.
  let matchingAttributes = agentBusinessAttributes.filter(function (
    entry: BusinessAttributes
  ) {
    if (
      entry.attributeName.toLowerCase() === "notes" &&
      callCustomerAttributes?.hasOwnProperty(entry.attributeName)
      // && callCustomerAttributes[entry.attributeName]
    ) {
      notesPresent = true;
      notesPropertySpelling = entry.attributeName;
      return false;
    }
    return callCustomerAttributes?.hasOwnProperty(entry.attributeName);
  });

  // Group the matching attributes into sets of 3 (three columns per row)
  let finalArray = [];
  for (let i = 0; i < matchingAttributes?.length; i += 2) {
    const attributes = [matchingAttributes[i], matchingAttributes[i + 1]];

    finalArray.push(
      <View key={`attribute-group-${i}`} marginTop={23}>
        <Flex direction="row" justifyContent={"space-between"} gap={50}>
          {attributes.map((attribute, index) =>
            attribute ? (
              <Flex
                direction="column"
                key={`attribute-${i + index}`}
                UNSAFE_style={{ flex: 1, minWidth: "47%", maxWidth: "47%" }} // Adjusts width for two columns
              >
                <Text
                  UNSAFE_className="call-detail-title"
                  UNSAFE_style={{
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {formatLabel(attribute.attributeName)}
                </Text>
                {formatLabel(attribute.attributeName) ===
                  "Aic Web Contact Monitored History" ? (
                  <Text
                    UNSAFE_className="call-detail-description"
                    UNSAFE_style={{
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {fixJson(
                      callCustomerAttributes[attribute.attributeName]
                    ).map((detail: any, idx: number) => (
                      <Flex
                        justifyContent={"space-between"}
                        marginTop={4}
                        key={idx}
                      >
                        <Text>{detail.user}</Text>
                        <Text>{formatTimestamp(detail.TS)}</Text>
                      </Flex>
                    ))}
                  </Text>
                ) : (
                  <Text
                    UNSAFE_className="call-detail-description"
                    UNSAFE_style={{
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {callCustomerAttributes[attribute.attributeName]}
                  </Text>
                )}
              </Flex>
            ) : null
          )}
        </Flex>
      </View>
    );
  }

  // Append Notes if Present
  if (notesPresent) {
    finalArray.push(
      <Flex direction="column" key="transfer-notes">
        <Text UNSAFE_className="call-detail-title">Transfer Notes</Text>
        <Text UNSAFE_className="call-detail-description">
          {callCustomerAttributes[notesPropertySpelling]}
        </Text>
      </Flex>
    );
  }

  useEffect(() => {
    if (agentList && agentList.length > 0) {
      setAgentDetails(agentList[0]);
    }
  }, [agentList]);

  useEffect(() => {
    const agent = agentList
      ? agentList.find(
        (agent: AgentStatusDetail) => agent.agent_ldap === selectedAgent
      )
      : {};
    setAgentDetails(agent);
    agent?.agent_current_contacts?.length > 0 &&
      !call.isSilentMonitor &&
      vpnStatus &&
      agentName !== selectedAgent &&
      !call.id &&
      agentDetails?.agent_current_contacts[0]?.contact_type !== "MONITOR"
      ? setIsDisabled(false)
      : setIsDisabled(true);
  }, [
    selectedAgent,
    agentList,
    call.isSilentMonitor,
    vpnStatus,
    agentDetails,
    call?.id,
  ]);

  useEffect(() => {
    const fetchCallDetails = async (contactId: string) => {
      try {
        const response = await supervisorCallDetails({
          contactId: contactId,
        });
        if (response?.status === "success") {
          setCallDetails(response?.callData);
        }
      } catch (error) {
        console.error("Failed to fetch call details", error);
      }
    };
    const newActiveContactId =
      (agentDetails?.agent_current_contacts?.length &&
        agentDetails?.agent_current_contacts[0]?.contact_id) ||
      "";
    if (newActiveContactId && newActiveContactId !== activeContactId) {
      setActiveContactId(newActiveContactId);
      fetchCallDetails(newActiveContactId);
    }
  }, [agentDetails, activeContactId]);

  const handleCallMonitoring = async () => {
    let response = null;
    const contactId =
      (agentDetails?.agent_current_contacts?.length &&
        agentDetails?.agent_current_contacts[0]?.contact_id) ||
      null;
    const payload = {
      contactId: contactId,
      monitoringType: monitorType,
    };
    if (contactId) {
      response = await callMonitorRequest(payload);
      if (response?.status === "success") {
        if (monitorType === "barge") {
          console.log("Starting barge-in with muteStatus:", muteStatus);
          // The escalateMonitorToBarge function will handle mute automatically after successful barge-in
          Emitter.emit(API_CALL_BARGE, {
            body: {
              conversationId: contactId,
              mute: muteStatus,
            },
          });
        }
        storeRTK.dispatch(setSelectedTab("Call"));
      } else {
        showErrorToast(
          "Call monitor request failed. Please try again",
          "monitorError"
        );
        Emitter.emit(TELEMETRY_ERROR, {
          type: "notif",
          chan: TELEMETRY_ERROR,
          body: {
            type: MONITORING_ERROR,
            chan: MONITORING_ERROR,
            message:
              monitorType +
              "call monitor request failed for contactId: " +
              contactId,
          },
        });
      }
    }
  };

  return (
    <>
      <View backgroundColor={"gray-100"} width={"64%"} height={"100%"}>
        <Flex direction={"column"} height={"100%"}>
          <Flex
            UNSAFE_className="call-monitor-detail"
            justifyContent="space-between"
            alignItems={"center"}
          >
            <Text UNSAFE_className="call-detail-heading">Call details</Text>
          </Flex>

          <Flex direction={"row"} height={"100%"}>
            <View width={"64%"} backgroundColor={"gray-100"} paddingX={32}>
              <Flex
                direction="row"
                justifyContent="space-between"
                height={"100%"}
              >
                {agentDetails?.agent_current_contacts?.length &&
                  agentDetails?.agent_current_contacts[0]?.contact_id ? (
                  <>
                    {finalArray.length > 0 ? (
                      <View marginTop={5} width={"100%"}>
                        {finalArray}
                      </View>
                    ) : (
                      <Flex
                        height={"100%"}
                        alignItems={"center"}
                        direction={"column"}
                        justifyContent={"center"}
                        width={"64%"}
                      >
                        {agentDetails?.agent_current_contacts?.length &&
                          agentDetails?.agent_current_contacts[0]?.contact_id ? (
                          <Text
                            UNSAFE_className="call-detail-title"
                            marginTop={10}
                          >
                            No details found
                          </Text>
                        ) : (
                          <ProgressCircle
                            aria-label="Loading…"
                            isIndeterminate
                          />
                        )}
                      </Flex>
                    )}
                  </>
                ) : (
                  <ActiveCallContainer
                    title="No active call"
                    description=" There is no active call with the agent"
                  />
                )}
              </Flex>
            </View>
            <View
              UNSAFE_className="call-monitor-wrapper"
              borderStartColor={"gray-200"}
              borderStartWidth={"thin"}
              height={"100%"}
              width={"36%"}
              backgroundColor={"gray-100"}
            >
              <CallMonitor
                monitorType={monitorType}
                setMonitorType={setMonitorType}
                handleCallMonitoring={handleCallMonitoring}
                isDisabled={isDisabled}
                muteStatus={muteStatus}
                setMuteStatus={setMuteStatus}
              />
            </View>
          </Flex>
        </Flex>
      </View>
    </>
  );
};

export default SupervisorCallDetails;
